/**
 * /payroll/tax-deposits — EFTPS deposit ledger.
 *
 * Tracks every payroll-tax deposit (941/940/945) so that 941 line 13a and
 * 940 line 13 reconcile against an actual deposit history.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { MoneyField } from "@/components/MoneyField";

export const Route = createFileRoute("/_auth/payroll/tax-deposits")({
  component: TaxDepositsPage,
});

interface Deposit {
  id: string;
  year: number;
  quarter: number | null;
  period_month: number | null;
  form_kind: string;
  deposit_date: string;
  amount: string;
  eftps_confirmation: string | null;
  payment_method: string;
  notes: string | null;
  created_at: string;
}

const fmtMoney = (s: string | number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(s));

const CURRENT_YEAR = new Date().getFullYear();

function TaxDepositsPage() {
  const qc = useQueryClient();
  const [yearFilter, setYearFilter] = useState(CURRENT_YEAR);
  const [formFilter, setFormFilter] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);

  // New-deposit form state
  const [form, setForm] = useState({
    year: CURRENT_YEAR,
    quarter: 1,
    period_month: new Date().getMonth() + 1,
    form_kind: "941",
    deposit_date: new Date().toISOString().slice(0, 10),
    amount: "",
    eftps_confirmation: "",
    payment_method: "eftps",
    notes: "",
  });

  const { data: deposits = [], isLoading } = useQuery({
    queryKey: ["payroll", "tax-deposits", yearFilter, formFilter],
    queryFn: async () => {
      const qs = new URLSearchParams({ year: String(yearFilter) });
      if (formFilter) qs.set("form_kind", formFilter);
      return (await api(`/api/payroll/year-end/deposits?${qs}`)) as Deposit[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      return await api(`/api/payroll/year-end/deposits`, {
        method: "POST",
        body: {
          year: form.year,
          quarter: form.form_kind === "941" ? form.quarter : null,
          period_month: form.period_month,
          form_kind: form.form_kind,
          deposit_date: form.deposit_date,
          amount: Number(form.amount),
          eftps_confirmation: form.eftps_confirmation || null,
          payment_method: form.payment_method,
          notes: form.notes || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll", "tax-deposits"] });
      setDialogOpen(false);
      setForm((f) => ({ ...f, amount: "", eftps_confirmation: "", notes: "" }));
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/payroll/year-end/deposits/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll", "tax-deposits"] }),
  });

  const totalShown = deposits.reduce((s, d) => s + Number(d.amount), 0);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Tax Deposits</Typography>
          <Typography variant="body2" color="text.secondary">
            EFTPS deposit ledger — feeds Form 941 line 13a and Form 940 line 13.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-year-end/tax-deposits.md" label="Tax deposits guide" />
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField select label="Year" size="small" value={yearFilter}
                     onChange={(e) => setYearFilter(Number(e.target.value))}
                     sx={{ minWidth: 110 }}>
            {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Form" size="small" value={formFilter}
                     onChange={(e) => setFormFilter(e.target.value)}
                     sx={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="941">941</MenuItem>
            <MenuItem value="940">940</MenuItem>
            <MenuItem value="945">945</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Record Deposit
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={3} mb={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">Total {yearFilter}{formFilter && ` ${formFilter}`}</Typography>
          <Typography variant="h5" fontWeight={600}>{fmtMoney(totalShown)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Deposits</Typography>
          <Typography variant="h5" fontWeight={600}>{deposits.length}</Typography>
        </Box>
      </Stack>

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Form</TableCell>
                <TableCell>Quarter</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>EFTPS Confirmation</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deposits.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.deposit_date}</TableCell>
                  <TableCell><Chip size="small" label={d.form_kind} /></TableCell>
                  <TableCell>{d.quarter ? `Q${d.quarter}` : "—"}</TableCell>
                  <TableCell>{d.payment_method}</TableCell>
                  <TableCell><code>{d.eftps_confirmation || "—"}</code></TableCell>
                  <TableCell>{d.notes || "—"}</TableCell>
                  <TableCell align="right">{fmtMoney(d.amount)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => remove.mutate(d.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {deposits.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No deposits recorded for {yearFilter}.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Tax Deposit</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField label="Year" type="number" size="small" value={form.year}
                       onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))} />
            <TextField select label="Form" size="small" value={form.form_kind}
                       onChange={(e) => setForm((f) => ({ ...f, form_kind: e.target.value }))}>
              <MenuItem value="941">941 (FIT/FICA)</MenuItem>
              <MenuItem value="940">940 (FUTA)</MenuItem>
              <MenuItem value="945">945 (Backup withholding)</MenuItem>
            </TextField>
            {form.form_kind === "941" && (
              <TextField select label="Quarter" size="small" value={form.quarter}
                         onChange={(e) => setForm((f) => ({ ...f, quarter: Number(e.target.value) }))}>
                {[1, 2, 3, 4].map((q) => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
              </TextField>
            )}
            <TextField select label="Period Month" size="small" value={form.period_month}
                       onChange={(e) => setForm((f) => ({ ...f, period_month: Number(e.target.value) }))}>
              {Array.from({ length: 12 }).map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>{new Date(2026, i, 1).toLocaleString("en-US", { month: "long" })}</MenuItem>
              ))}
            </TextField>
            <TextField label="Deposit Date" type="date" size="small" value={form.deposit_date}
                       InputLabelProps={{ shrink: true }}
                       onChange={(e) => setForm((f) => ({ ...f, deposit_date: e.target.value }))} />
            <MoneyField label="Amount" size="small" value={form.amount}
                       onChange={(v) => setForm((f) => ({ ...f, amount: v }))} />
            <TextField select label="Method" size="small" value={form.payment_method}
                       onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}>
              <MenuItem value="eftps">EFTPS</MenuItem>
              <MenuItem value="ach">ACH</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="wire">Wire</MenuItem>
            </TextField>
            <TextField label="EFTPS Confirmation" size="small" value={form.eftps_confirmation}
                       onChange={(e) => setForm((f) => ({ ...f, eftps_confirmation: e.target.value }))}
                       sx={{ gridColumn: "span 2" }} />
            <TextField label="Notes" size="small" multiline rows={2} value={form.notes}
                       onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                       sx={{ gridColumn: "span 2" }} />
          </Box>
          {create.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String((create.error as Error).message)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => create.mutate()}
                  disabled={!form.amount || create.isPending}>
            {create.isPending ? <CircularProgress size={18} /> : "Record"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
